import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

/**
 * Emotion Trends Chart - Shows last 10 sessions with 5 emotion metrics
 * @param {Object} props
 * @param {Array} props.data - Array of emotion trend data objects
 * @param {Object} props.style - Optional style overrides
 */
export function EmotionTrendsChart({ data, style }) {
  if (!data || data.length === 0) return null;

  return (
    <div className="chart-section" style={style}>
      <h3>Emotion Trends (Last 10 Sessions)</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="session" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="joy" stroke="#28a745" strokeWidth={2} name="Joy" />
          <Line type="monotone" dataKey="stress" stroke="#dc3545" strokeWidth={2} name="Stress" />
          <Line type="monotone" dataKey="sadness" stroke="#6c757d" strokeWidth={2} name="Sadness" />
          <Line type="monotone" dataKey="anger" stroke="#fd7e14" strokeWidth={2} name="Anger" />
          <Line type="monotone" dataKey="burnout" stroke="#6f42c1" strokeWidth={3} name="Burnout" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Risk Analysis Panel - Shows 4 risk component bars and cumulative risk
 * @param {Object} props
 * @param {Object} props.riskComponents - Object with emotional_anomaly, behavioral_deviation, burnout, intent_score
 * @param {number} props.cumulativeRisk - Cumulative risk percentage
 * @param {string} props.healthStatus - Optional health status text
 */
export function RiskAnalysisPanel({ riskComponents, cumulativeRisk, healthStatus }) {
  if (!riskComponents) return null;

  const riskItems = [
    { label: 'Emotional Anomaly', value: riskComponents.emotional_anomaly },
    { label: 'Behavioral Deviation', value: riskComponents.behavioral_deviation },
    { label: 'Burnout', value: riskComponents.burnout },
    { label: 'Intent Score', value: riskComponents.intent_score }
  ];

  return (
    <>
      <div className="risk-components">
        {riskItems.map((item, index) => (
          <div className="risk-item" key={index}>
            <span>{item.label}:</span>
            <div className="risk-bar">
              <div className="risk-fill" style={{ width: `${item.value}%` }}></div>
            </div>
            <span>{item.value}%</span>
          </div>
        ))}
      </div>
      <div className="cumulative-risk">
        <h4>Cumulative Risk: {Math.round(cumulativeRisk)}%</h4>
        {healthStatus && (
          <p className={`risk-status ${healthStatus.toLowerCase().replace(' ', '-')}`}>
            {healthStatus}
          </p>
        )}
      </div>
    </>
  );
}

/**
 * Mental State Display - Shows mental state badge, score, and summary
 * @param {Object} props
 * @param {Object} props.mentalState - Object with state, score, color, summary
 * @param {Object} props.style - Optional style overrides
 */
export function MentalStateDisplay({ mentalState, style }) {
  if (!mentalState) return null;

  return (
    <div className="mental-state" style={style}>
      <h3>Mental State</h3>
      <div className="mental-state-content">
        <div className="mental-state-badge" style={{ backgroundColor: mentalState.color }}>
          {mentalState.state}
        </div>
        <div className="mental-state-score">
          <span>Score: {Math.round(mentalState.score)}/100</span>
        </div>
        <div className="mental-state-summary">
          <p>{mentalState.summary}</p>
        </div>
      </div>
    </div>
  );
}
